import React, { useId } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import OverflowText from './OverflowText';
import { EM_DASH } from '../../utils/formatDate';

/**
 * ReadOnlyField — gray read-only display with the same label as FormField
 * (UI Design Standards §4.2). A div, not a disabled input, so the text can
 * be selected and copied. Empty values show "—".
 *
 * Props:
 *   label      {node}
 *   value      {node}
 *   multiline  {bool}  wrap instead of ellipsis + tooltip
 */
const ReadOnlyField = ({ label, value, multiline = false, sx }) => {
  const { ft } = useTheme().palette;
  const labelId = `${useId()}-label`;
  const isEmpty = value === null || value === undefined || value === '';

  let content;
  if (isEmpty) {
    content = (
      <Box component="span" sx={{ color: ft.neutrals.textDisabled }}>
        {EM_DASH}
      </Box>
    );
  } else if (multiline) {
    content = (
      <Box sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{value}</Box>
    );
  } else {
    content = (
      <OverflowText
        variant="body2"
        component="span"
        sx={{ flex: 1, fontSize: 14, color: 'inherit' }}
      >
        {value}
      </OverflowText>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0, ...sx }}>
      {label && (
        <Typography
          variant="fieldLabel"
          component="span"
          id={labelId}
          sx={{ color: ft.neutrals.textBody }}
        >
          {label}
        </Typography>
      )}
      <Box
        aria-labelledby={label ? labelId : undefined}
        sx={{
          display: 'flex',
          alignItems: multiline ? 'flex-start' : 'center',
          minWidth: 0,
          minHeight: 40,
          boxSizing: 'border-box',
          px: 1.5,
          py: 1,
          fontSize: 14,
          color: ft.neutrals.textBody,
          backgroundColor: ft.neutrals.headerRow,
          border: `1px solid ${ft.neutrals.borderLight}`,
          borderRadius: `${ft.radius.control}px`,
        }}
      >
        {content}
      </Box>
    </Box>
  );
};

export default ReadOnlyField;
